import { identityT } from 'helpers/i18nFallback';
// Presentation metadata for the seeded `core-compliance` checklist items.
//
// The API (`GET /guiding-checklists`) gives us name, description, steps,
// videoUrl, isOptional and completed — everything except the three things the
// focused onboarding panel needs: a short rail label, the destination the CTA
// should send the user to, and a rough effort estimate.
//
// Keyed on the item `key`, which the backend guarantees is immutable
// (docs/features/guiding-checklists/README.md — "Never change a slug or item
// key after deployment"), so this map is safe to key on. Unknown keys fall back
// to the item's own name and open the detail modal instead of navigating.
//
// If product wants to edit these without a frontend deploy, move `short`,
// `route` and `minutes` into the seed's item objects and read them off the API
// response instead — `getItemMeta` is the only place that would change.

// The four data steps all send you to the Upload hub, not to their own pages.
// One CG workbook's sheets import suppliers, installations, imports AND
// emissions in a single pass (src/modules/UploadHub/docs/README.md), and the
// EU Communication sheet covers supplier emissions — so the hub is the short
// way through all four. The pages themselves are still there in the sidebar for
// anyone who wants to add records by hand.
// A builder rather than a constant: `cta` and `short` are rendered, so they
// come from `t`. Keys are namespaced explicitly because there is no enclosing
// useTranslation() here (docs/features/declarant-i18n/CONVENTIONS.md).
//
// `route` and `minutes` are structural and stay literal, which is why callers
// that only want `minutes` can omit `t` entirely.
export const buildChecklistItemMeta = (t) => ({
  'upload-suppliers': {
    short: t('onboarding:checklist.item.uploadSuppliers.short', 'Suppliers'),
    cta: t('onboarding:checklist.item.uploadSuppliers.cta', 'Upload suppliers'),
    route: '/upload-hub',
    minutes: 3,
  },
  'upload-installations': {
    short: t('onboarding:checklist.item.uploadInstallations.short', 'Installations'),
    cta: t('onboarding:checklist.item.uploadInstallations.cta', 'Upload installations'),
    route: '/upload-hub',
    minutes: 3,
  },
  'upload-imports': {
    short: t('onboarding:checklist.item.uploadImports.short', 'Imports'),
    cta: t('onboarding:checklist.item.uploadImports.cta', 'Upload imports'),
    route: '/upload-hub',
    minutes: 4,
  },
  'upload-emissions': {
    short: t('onboarding:checklist.item.uploadEmissions.short', 'Emissions'),
    cta: t('onboarding:checklist.item.uploadEmissions.cta', 'Upload emissions'),
    route: '/upload-hub',
    minutes: 10,
  },
  'calculate-sefa': {
    short: t('onboarding:checklist.item.calculateSefa.short', 'SEFA'),
    cta: t('onboarding:checklist.item.calculateSefa.cta', 'Calculate SEFA'),
    route: '/analytics-overview',
    minutes: 2,
  },
  'generate-report': {
    short: t('onboarding:checklist.item.generateReport.short', 'Report'),
    cta: t('onboarding:checklist.item.generateReport.cta', 'Generate report'),
    route: '/reports',
    minutes: 2,
  },
  'connect-supplier': {
    short: t('onboarding:checklist.item.connectSupplier.short', 'Connect'),
    cta: t('onboarding:checklist.item.connectSupplier.cta', 'Invite supplier'),
    route: '/suppliers',
    minutes: 1,
  },
});

const FALLBACK_MINUTES = 5;

/**
 * @param {{key:string,name:string}} item
 * @param {Function} [t]  omit when only `route`/`minutes` are wanted
 * @returns {{short:string,cta:string,route:string|null,minutes:number}}
 */
export const getItemMeta = (item, t = identityT) => {
  const meta = item?.key ? buildChecklistItemMeta(t)[item.key] : null;
  if (meta) return meta;
  return {
    short: item?.name ?? t('onboarding:checklist.item.fallback.short', 'Step'),
    cta: t('onboarding:checklist.item.fallback.cta', 'Open step'),
    route: null,
    minutes: FALLBACK_MINUTES,
  };
};

/** Summed effort of everything not yet completed, in whole minutes. */
export const estimateRemainingMinutes = (items = []) =>
  items
    .filter((item) => !item.completed)
    .reduce((total, item) => total + getItemMeta(item).minutes, 0);
